import { create } from 'zustand'

/**
 * The in-flight second-factor challenge.
 *
 * Memory only, on purpose: no `persist`, no devtools middleware (which would
 * mirror the token into an inspectable log). A reload drops the challenge and
 * the visitor starts again from the password step — which is the contract.
 */
export interface TwoFactorChallenge {
  kind: 'verify' | 'setup'
  token: string
  /** Epoch ms after which the backend rejects the token. */
  expiresAt: number
}

/** Why the visitor was sent back to the password step. */
export type ChallengeNotice = 'expired'

interface TwoFactorChallengeState {
  challenge: TwoFactorChallenge | null
  notice: ChallengeNotice | null
  begin: (kind: TwoFactorChallenge['kind'], token: string, expiresInSeconds: number) => void
  /** Drop the challenge, optionally leaving a notice for the login page. */
  end: (notice?: ChallengeNotice) => void
  clearNotice: () => void
}

export const useTwoFactorChallengeStore = create<TwoFactorChallengeState>()((set) => ({
  challenge: null,
  notice: null,
  begin: (kind, token, expiresInSeconds) =>
    set({ challenge: { kind, token, expiresAt: Date.now() + expiresInSeconds * 1000 }, notice: null }),
  end: (notice) => set({ challenge: null, notice: notice ?? null }),
  clearNotice: () => set({ notice: null }),
}))
